import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../../components/ui/button';
import { MapLocation } from './types';

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)['_getIconUrl'];
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function FitBounds({ locations }: { locations: MapLocation[] }) {
  const map = useMap();
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map((l) => [l.lat, l.lng]));
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 6 });
    }
  }, [locations, map]);
  return null;
}

/**
 * CARTO basemaps need a key: without one every tile is stamped "API KEY
 * REQUIRED". The key is set per instance as the `REACT_APP_CARTO_BASEMAPS_KEY`
 * Heroku config var (baked in at build); local dev reads it from
 * `frontend/.env.local`. With no key the URL is unchanged from before.
 */
const CARTO_BASEMAPS_KEY = process.env['REACT_APP_CARTO_BASEMAPS_KEY'];
const TILE_URL =
  'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png' +
  (CARTO_BASEMAPS_KEY ? `?key=${encodeURIComponent(CARTO_BASEMAPS_KEY)}` : '');

interface SupplierMapProps {
  locations: MapLocation[];
  onViewDetails: (id: number) => void;
}

export function SupplierMap({ locations, onViewDetails }: SupplierMapProps) {
  return (
    <div className="h-64 rounded-lg overflow-hidden border relative z-0" data-tour="supplier-map">
      <MapContainer
        center={[30, 10]}
        zoom={2}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <FitBounds locations={locations} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
          url={TILE_URL}
        />
        {locations.map((location) => (
          <Marker key={location.id} position={[location.lat, location.lng]}>
            <Popup>
              <div className="font-medium">{location.name}</div>
              <Button
                size="sm"
                variant="link"
                className="p-0 h-auto"
                onClick={() => onViewDetails(location.id)}
              >
                View Details
              </Button>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
